// Performance Validation Script
// Add this to your browser console to validate optimizations

export const validatePerformanceOptimizations = () => {
    console.log('🚀 Performance Optimization Validation Starting...');

    const results = {
        apiCaching: false,
        memoryManagement: false,
        bundleSize: false,
        serviceWorker: false,
        progressiveLoading: false,
        overallScore: 0
    };

    // 1. Check API Caching
    try {
        const apiService = (window as any).apiService;
        if (apiService && apiService.getCacheStatus) {
            const cacheStatus = apiService.getCacheStatus();
            results.apiCaching = cacheStatus && cacheStatus.size > 0;
            console.log('✅ API Caching:', results.apiCaching ? 'Working' : 'Not Working');
        } else {
            console.log('⚠️ API Service not available for testing');
        }
    } catch (error) {
        console.log('❌ API Caching Test Failed:', error);
    }

    // 2. Check Memory Management
    try {
        const memoryManager = (window as any).MemoryManager?.getInstance();
        if (memoryManager) {
            const memoryReport = memoryManager.getMemoryReport();
            const memoryUsage = performance.memory?.usedJSHeapSize || 0;
            results.memoryManagement = memoryUsage < 30 * 1024 * 1024; // < 30MB
            console.log('✅ Memory Management:', results.memoryManagement ? 'Good' : 'High Usage');
            console.log('📊 Memory Usage:', `${(memoryUsage / 1024 / 1024).toFixed(2)} MB`);
        } else {
            console.log('⚠️ Memory Manager not available for testing');
        }
    } catch (error) {
        console.log('❌ Memory Management Test Failed:', error);
    }

    // 3. Check Bundle Size
    try {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const bundleSize = navigation.transferSize || 0;
        results.bundleSize = bundleSize < 2 * 1024 * 1024; // < 2MB
        console.log('✅ Bundle Size:', results.bundleSize ? 'Good' : 'Too Large');
        console.log('📊 Bundle Size:', `${(bundleSize / 1024 / 1024).toFixed(2)} MB`);
    } catch (error) {
        console.log('❌ Bundle Size Test Failed:', error);
    }

    // 4. Check Service Worker
    try {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then(registrations => {
                results.serviceWorker = registrations.length > 0;
                console.log('✅ Service Worker:', results.serviceWorker ? 'Active' : 'Not Active');
            });
        } else {
            console.log('⚠️ Service Workers not supported');
        }
    } catch (error) {
        console.log('❌ Service Worker Test Failed:', error);
    }

    // 5. Check Progressive Loading
    try {
        const skeletonElements = document.querySelectorAll('[class*="skeleton"], [class*="loading"]');
        results.progressiveLoading = skeletonElements.length > 0;
        console.log('✅ Progressive Loading:', results.progressiveLoading ? 'Working' : 'Not Found');
    } catch (error) {
        console.log('❌ Progressive Loading Test Failed:', error);
    }

    // 6. Performance Metrics
    try {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const fcp = performance.getEntriesByType('paint').find(entry => entry.name === 'first-contentful-paint')?.startTime || 0;
        const tti = navigation.loadEventEnd - navigation.loadEventStart;

        console.log('📊 Performance Metrics:');
        console.log('   First Contentful Paint:', `${fcp.toFixed(2)}ms`);
        console.log('   Time to Interactive:', `${tti.toFixed(2)}ms`);
        console.log('   Load Event End:', `${navigation.loadEventEnd.toFixed(2)}ms`);

        const performanceGood = fcp < 2000 && tti < 3000;
        console.log('✅ Performance:', performanceGood ? 'Good' : 'Needs Improvement');
    } catch (error) {
        console.log('❌ Performance Metrics Test Failed:', error);
    }

    // Calculate overall score
    const tests = Object.values(results).filter(v => typeof v === 'boolean');
    const passedTests = tests.filter(v => v).length;
    results.overallScore = (passedTests / tests.length) * 100;

    console.log('\n🎯 Validation Results:');
    console.log('=====================');
    console.log(`Overall Score: ${results.overallScore.toFixed(1)}%`);
    console.log(`Tests Passed: ${passedTests}/${tests.length}`);

    if (results.overallScore >= 80) {
        console.log('🎉 Excellent! Performance optimizations are working well!');
    } else if (results.overallScore >= 60) {
        console.log('👍 Good! Most optimizations are working, some improvements needed.');
    } else {
        console.log('⚠️ Needs attention! Several optimizations may not be working properly.');
    }

    return results;
};

// Real-time monitoring
export const startPerformanceMonitoring = () => {
    console.log('🔍 Starting real-time performance monitoring...');

    let memoryUsage: number[] = [];
    let apiResponseTimes: number[] = [];

    // Monitor memory usage
    const memoryInterval = setInterval(() => {
        if (performance.memory) {
            const usage = performance.memory.usedJSHeapSize / 1024 / 1024;
            memoryUsage.push(usage);

            if (memoryUsage.length > 10) {
                memoryUsage.shift();
            }

            const avgMemory = memoryUsage.reduce((a, b) => a + b, 0) / memoryUsage.length;
            console.log(`📊 Memory Usage: ${usage.toFixed(2)}MB (Avg: ${avgMemory.toFixed(2)}MB)`);

            // Alert if memory usage is high
            if (usage > 30) {
                console.warn('⚠️ High memory usage detected!');
            }
        }
    }, 5000);

    // Monitor API performance
    const originalFetch = window.fetch;
    window.fetch = function (...args) {
        const startTime = performance.now();
        return originalFetch.apply(this, args).then(response => {
            const responseTime = performance.now() - startTime;
            apiResponseTimes.push(responseTime);

            if (apiResponseTimes.length > 10) {
                apiResponseTimes.shift();
            }

            const avgResponseTime = apiResponseTimes.reduce((a, b) => a + b, 0) / apiResponseTimes.length;
            console.log(`📊 API Response: ${responseTime.toFixed(2)}ms (Avg: ${avgResponseTime.toFixed(2)}ms)`);

            // Alert if response time is slow
            if (responseTime > 500) {
                console.warn('⚠️ Slow API response detected!');
            }

            return response;
        });
    };

    console.log('✅ Performance monitoring started. Check console for real-time metrics.');

    // Return cleanup function
    return () => {
        clearInterval(memoryInterval);
        window.fetch = originalFetch;
        console.log('🛑 Performance monitoring stopped.');
    };
};

// Quick validation function
export const quickValidation = () => {
    console.log('⚡ Quick Performance Validation...');

    // Check if app loads fast
    const loadTime = performance.now();
    console.log(`🚀 App Load Time: ${loadTime.toFixed(2)}ms`);

    // Check memory usage
    if (performance.memory) {
        const memory = performance.memory.usedJSHeapSize / 1024 / 1024;
        console.log(`💾 Memory Usage: ${memory.toFixed(2)}MB`);
    }

    // Check if service worker is active
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
            console.log(`🔧 Service Worker: ${registrations.length > 0 ? 'Active' : 'Not Active'}`);
        });
    }

    console.log('✅ Quick validation complete!');
};

// Export for global use
if (typeof window !== 'undefined') {
    (window as any).validatePerformanceOptimizations = validatePerformanceOptimizations;
    (window as any).startPerformanceMonitoring = startPerformanceMonitoring;
    (window as any).quickValidation = quickValidation;
} 